"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowRight, Database, FileText, Users2, TrendingUp } from "lucide-react";
import DashboardMockup from "./DashboardMockup";
import { fadeUp, heroFadeUp, staggerContainer } from "@/lib/motion";

const stats = [
  { icon: Database, value: "14.7M+", label: "Data Records" },
  { icon: FileText, value: "500+", label: "Datasets" },
  { icon: Users2, value: "12,400+", label: "API Users" },
  { icon: TrendingUp, value: "99.9%", label: "Uptime SLA" },
];

export default function Hero() {
  return (
    <section className="bg-white px-3 pt-3 sm:px-4 sm:pt-4">
      <div className="relative mx-auto max-w-7xl overflow-hidden rounded-3xl bg-gradient-to-br from-white via-cream/50 to-white">
        {/* radial warm glow behind the mockup */}
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-brand-light/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 right-1/3 h-72 w-72 rounded-full bg-brand-light/20 blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 py-16 sm:px-10 lg:grid-cols-2 lg:gap-8 lg:px-14 lg:py-20">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="flex flex-col items-center text-center lg:items-start lg:text-left"
          >
            <motion.h1
              variants={heroFadeUp}
              className="text-3xl font-semibold leading-[1.15] tracking-tight text-ink sm:text-4xl lg:text-5xl lg:leading-[1.1] xl:text-[3.25rem]"
            >
              The Medtechdata Agency Portal Built for Modern Teams
            </motion.h1>
            <motion.p
              variants={heroFadeUp}
              custom={1}
              className="mt-6 max-w-lg text-base leading-relaxed text-ink-soft"
            >
              Access 14.7M+ verified records across 500+ datasets. Integrate
              in minutes with our clean RESTful API. Scale from prototype to
              production with confidence.
            </motion.p>
            <motion.div
              variants={heroFadeUp}
              custom={2}
              className="mt-8 flex w-full flex-col items-center gap-3 sm:w-auto sm:flex-row sm:gap-4"
            >
              <Link href="/dashboard" className="w-full sm:w-auto">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="w-full rounded-lg border border-black/10 bg-white px-5 py-3 text-sm font-medium text-ink shadow-sm transition-colors hover:bg-black/5 sm:w-auto"
                >
                  View Dashboard demo
                </motion.button>
              </Link>
              <Link href="/signup" className="w-full sm:w-auto">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="group flex w-full items-center justify-center gap-2 rounded-lg bg-[linear-gradient(93.49deg,#E86C26_0.81%,#ED8951_49.4%,#BA561E_96.11%)] px-5 py-3 text-sm font-medium text-white shadow-lg shadow-brand/30 transition-opacity hover:opacity-90 sm:w-auto"
                >
                  Start free Trial
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </motion.button>
              </Link>
            </motion.div>
            <motion.p
              variants={heroFadeUp}
              custom={3}
              className="mt-4 text-xs text-ink-soft"
            >
              No credit card required &middot; 14-day free trial &middot;
              Cancel anytime
            </motion.p>
          </motion.div>

          <DashboardMockup />
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-white">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          className="mx-auto grid max-w-7xl grid-cols-2 gap-y-12 gap-x-8 px-6 py-20 sm:grid-cols-4 lg:px-8"
        >
          {stats.map((stat) => (
            <motion.div
              key={stat.label}
              variants={fadeUp}
              className="flex flex-col items-center gap-6 text-center"
            >
              <span className="flex h-18 w-18 items-center justify-center rounded-3xl bg-brand/10 text-brand">
                <stat.icon className="h-7 w-7 stroke-[1.5]" />
              </span>
              <div className="flex flex-col gap-1.5">
                <span className="font-heading text-4xl font-semibold text-ink sm:text-5xl">
                  {stat.value}
                </span>
                <span className="font-heading text-[15px] font-medium text-ink-soft">
                  {stat.label}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
